import { useNavigate } from '@tanstack/react-router';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@upme/ui/components/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@upme/ui/components/table';
import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { EmptyState } from '../../../shared/components/empty-state';
import { type AdminNewsRow, useConvertNewsToEvent, useUpdateNewsStatus } from '../queries';
import { DeleteNewsDialog } from './delete-news-dialog';

interface Props {
  rows: ReadonlyArray<AdminNewsRow>;
}

const dateFmt = new Intl.DateTimeFormat('ru-RU', {
  dateStyle: 'short',
  timeStyle: 'short',
});

function ActionsCell({ row }: { row: AdminNewsRow }) {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const statusMutation = useUpdateNewsStatus();
  const navigate = useNavigate();
  const convertMutation = useConvertNewsToEvent();

  const toggleStatus = async () => {
    const next = row.status === 'PUBLISHED' ? 'HIDDEN' : 'PUBLISHED';
    try {
      await statusMutation.mutateAsync({ id: row.id, status: next });
      toast.success(next === 'HIDDEN' ? 'Новость скрыта' : 'Новость опубликована');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Не удалось изменить статус';
      toast.error(message);
    }
  };

  const convertToEvent = async () => {
    try {
      const { eventId } = await convertMutation.mutateAsync(row.id);
      toast.success('Новость перенесена в события (черновик)');
      navigate({ to: '/events/$eventId', params: { eventId } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось перенести');
    }
  };

  return (
    <>
      <DeleteNewsDialog news={row} open={deleteOpen} onOpenChange={setDeleteOpen} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={toggleStatus} disabled={statusMutation.isPending}>
            {row.status === 'PUBLISHED' ? 'Скрыть' : 'Показать'}
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a href={row.sourceUrl} target="_blank" rel="noopener noreferrer">
              Открыть оригинал
            </a>
          </DropdownMenuItem>
          {row.sourceMessageId && (
            <DropdownMenuItem onClick={convertToEvent} disabled={convertMutation.isPending}>
              Перенести в события
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => setDeleteOpen(true)}
          >
            Удалить
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

export function NewsTable({ rows }: Props) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="Новостей нет"
        description="Новости появятся здесь после того, как будут автоматически опубликованы из Telegram-каналов."
      />
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-10">Обл.</TableHead>
          <TableHead>Заголовок</TableHead>
          <TableHead>Канал</TableHead>
          <TableHead>Опубликовано</TableHead>
          <TableHead>Статус</TableHead>
          <TableHead className="text-right">Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell>
              {row.coverStorageKey ? (
                <span title="Есть обложка">📰</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell className="max-w-[320px]">
              <p className="line-clamp-2 text-sm">{row.title}</p>
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">{row.sourceChannel?.title ?? '—'}</TableCell>
            <TableCell className="tabular-nums text-sm whitespace-nowrap">
              {dateFmt.format(new Date(row.publishedAt))}
            </TableCell>
            <TableCell>
              {row.status === 'PUBLISHED' ? (
                <Badge variant="default">Опубликовано</Badge>
              ) : (
                <Badge variant="secondary">Скрыто</Badge>
              )}
            </TableCell>
            <TableCell className="text-right">
              <ActionsCell row={row} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
